/**
 * PrinterPaperSizeSelector — Selector visual del ancho de papel térmico (58 mm / 80 mm).
 *
 * QUÉ HACE:
 *  - Renderiza dos «mini tickets» a escala (uno angosto 58 mm y uno ancho 80 mm)
 *    con líneas de texto simuladas para que el usuario perciba la diferencia REAL
 *    de ancho de impresión, no sólo la etiqueta numérica.
 *  - Cada tarjeta es seleccionable; muestra el estado activo con borde primario,
 *    halo y un check.
 *
 * QUÉ EXPORTA: PrinterPaperSizeSelector (named), PrinterPaperWidth (type)
 *
 * QUÉ NO DEBE HACER:
 *  - No persiste estado: es controlado (`value` + `onChange`).
 *  - No conoce de impresoras concretas ni transports: sólo de mm.
 *  - No realiza llamadas a API ni a hardware nativo.
 *
 * REGLAS:
 *  - Si en el futuro aparece un nuevo formato (ej. 110 mm), AGREGAR aquí en
 *    PAPER_OPTIONS, no duplicar el componente en otros archivos.
 *  - Las proporciones (58 vs 80) deben mantenerse aproximadamente reales para
 *    que la comparativa siga siendo útil.
 */
import { Check } from "lucide-react";

export type PrinterPaperWidth = 58 | 80;

interface Option {
  value: PrinterPaperWidth;
  /** Ancho relativo del mini-ticket (en % del contenedor); refleja proporción real 58/80. */
  widthPct: number;
  /** Nº de líneas largas vs cortas para que se note la diferencia visual. */
  longLines: number;
  shortLines: number;
  caption: string;
}

const PAPER_OPTIONS: Option[] = [
  {
    value: 58,
    widthPct: 72.5, // 58/80 ≈ 0.725
    longLines: 2,
    shortLines: 3,
    caption: "Ticket compacto. Ideal para tiendas, cafés y caja portátil.",
  },
  {
    value: 80,
    widthPct: 100,
    longLines: 4,
    shortLines: 2,
    caption: "Ticket ancho. Más espacio para logos, ítems y totales grandes.",
  },
];

interface Props {
  value: PrinterPaperWidth;
  onChange: (next: PrinterPaperWidth) => void;
  className?: string;
}

export const PrinterPaperSizeSelector = ({
  value,
  onChange,
  className = "",
}: Props) => {
  return (
    <div className={`grid grid-cols-2 gap-3 ${className}`}>
      {PAPER_OPTIONS.map((opt) => {
        const on = value === opt.value;
        const tagLabel = opt.value === 58 ? "Compacto" : "Ancho";
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={on}
            className={`group flex flex-col rounded-2xl border-2 p-3 text-left transition-all
              focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30
              ${
                on
                  ? "border-primary-600 bg-primary-50/70 dark:bg-primary-950/30 shadow-sm"
                  : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 hover:border-gray-300 dark:hover:border-slate-500"
              }`}
          >
            {/* Encabezado: tag + check (espacio propio, no se solapa con el ticket) */}
            <div className="flex items-center justify-between gap-2 mb-2">
              <span
                className={`text-[9px] font-bold uppercase tracking-wider ${
                  on
                    ? "text-primary-600 dark:text-primary-300"
                    : "text-gray-400 dark:text-slate-500"
                }`}
              >
                {tagLabel}
              </span>
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center transition-all
                  ${
                    on
                      ? "bg-primary-600 text-white scale-100"
                      : "bg-gray-100 dark:bg-slate-700 text-transparent scale-90"
                  }`}
                aria-hidden
              >
                <Check size={12} strokeWidth={3} />
              </span>
            </div>

            {/* Mini ticket simulado: el ancho refleja la proporción real */}
            <div
              className="relative mx-auto h-20 rounded-md bg-white dark:bg-slate-100 shadow-inner overflow-hidden border border-gray-200 dark:border-slate-300"
              style={{ width: `${opt.widthPct}%` }}
              aria-hidden
            >
              <div className="flex items-center justify-center pt-1.5">
                <div className="w-3 h-3 rounded-full bg-gray-400 dark:bg-slate-500" />
              </div>
              <div className="px-1.5 pt-1 space-y-1">
                {Array.from({ length: opt.longLines }).map((_, i) => (
                  <div
                    key={`l-${i}`}
                    className="h-[3px] rounded-full bg-gray-300 dark:bg-slate-400"
                  />
                ))}
                {Array.from({ length: opt.shortLines }).map((_, i) => (
                  <div
                    key={`s-${i}`}
                    className="h-[3px] rounded-full bg-gray-200 dark:bg-slate-300"
                    style={{ width: `${65 - i * 8}%` }}
                  />
                ))}
              </div>
              <div className="absolute bottom-1 left-1 right-1 h-[5px] rounded-full bg-gray-700 dark:bg-slate-700" />
            </div>

            {/* Etiqueta y descripción centrada bajo el ticket */}
            <div className="mt-2.5 text-center">
              <span
                className={`text-sm font-black ${
                  on
                    ? "text-primary-700 dark:text-primary-300"
                    : "text-gray-700 dark:text-slate-300"
                }`}
              >
                {opt.value} mm
              </span>
            </div>
            <p className="mt-1 text-[10px] leading-snug text-gray-500 dark:text-slate-400 text-center">
              {opt.caption}
            </p>
          </button>
        );
      })}
    </div>
  );
};
